import { Link } from 'react-router-dom';
import { heroUrls, categories, articles, faqs, siteConfig } from '@/mocks/navigation';

function StatusBadge({ status }: { status: string }) {
  const isOnline = status === '正常';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
        isOnline
          ? 'bg-accent-100 text-accent-700'
          : 'bg-secondary-100 text-secondary-700'
      }`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${isOnline ? 'bg-accent-500' : 'bg-secondary-400'}`}></span>
      {status}
    </span>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-50/60 via-background-50 to-background-50"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-accent-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4"></div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 leading-tight tracking-tight">
              {siteConfig.name}
            </h1>
            <p className="mt-1 text-lg md:text-xl text-primary-600 font-medium">
              {siteConfig.tagline}
            </p>
            <p className="mt-4 text-base md:text-lg text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {siteConfig.description}
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category/latest-urls"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-link text-lg"></i>
                查看最新网址
              </Link>
              <Link
                to="/category/tutorials"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-800 rounded-full text-sm font-semibold hover:bg-background-200 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-book-open-line text-lg"></i>
                访问教程
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest URLs Section */}
      <section className="py-14 md:py-20">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                最新可用网址
              </h2>
              <p className="mt-2 text-sm text-foreground-500">实时监控，定时更新，确保每个入口可用</p>
            </div>
            <Link
              to="/category/latest-urls"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors duration-200"
            >
              查看全部 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-product-shop>
            {heroUrls.map((item) => (
              <a
                key={item.id}
                href={item.url}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group block p-5 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 truncate">
                        {item.title}
                      </h3>
                      {item.isRecommended && (
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap">
                          推荐
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-foreground-500 truncate">{item.url}</p>
                  </div>
                  <StatusBadge status={item.status} />
                </div>
                <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
                  <span className="flex items-center gap-1">
                    <i className="ri-speed-line"></i>
                    {item.speed}
                  </span>
                  <span>更新于 {item.updatedAt}</span>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Link
              to="/category/latest-urls"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              查看全部网址 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">网址分类导航</h2>
            <p className="mt-2 text-sm text-foreground-500">按类别浏览，快速找到您需要的入口</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col p-6 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all duration-300">
                  <i className={`${cat.icon} text-lg`}></i>
                </div>
                <h3 className="mt-4 text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200">
                  {cat.name}
                </h3>
                <p className="mt-1 text-sm text-foreground-500 leading-relaxed flex-1">
                  {cat.description}
                </p>
                <span className="mt-3 text-xs text-foreground-400">
                  共 {cat.count} 个入口
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-14 md:py-20">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">最新文章</h2>
              <p className="mt-2 text-sm text-foreground-500">访问教程、使用技巧和最新动态</p>
            </div>
            <Link
              to="/category/tutorials"
              className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors duration-200"
            >
              更多文章 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {articles.slice(0, 3).map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group block bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs font-medium rounded-full whitespace-nowrap">
                      {article.categoryName}
                    </span>
                    <span className="text-xs text-foreground-400">{article.readTime}</span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.publishedAt}</span>
                    <span className="flex items-center gap-1 group-hover:text-primary-600 transition-colors">
                      阅读全文 <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-100/60">
        <div className="container-custom">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">常见问题</h2>
            <p className="mt-2 text-sm text-foreground-500">关于草榴社区网址导航的常见疑问解答</p>
          </div>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqs.slice(0, 4).map((faq, index) => (
              <details
                key={index}
                className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
              >
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                  <h4 className="text-sm font-semibold text-foreground-900 pr-4">
                    {faq.question}
                  </h4>
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 group-open:rotate-45 transition-transform duration-200">
                    <i className="ri-add-line text-sm"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/about"
              className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap transition-colors duration-200"
            >
              查看更多常见问题 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 md:py-20">
        <div className="container-custom">
          <div className="relative rounded-2xl bg-primary-500 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-transparent"></div>
            <div className="relative z-10 px-6 py-12 md:py-16 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-background-50">
                收藏本页，访问不迷路
              </h2>
              <p className="mt-3 text-base text-primary-100 max-w-xl mx-auto">
                按 Ctrl+D (Windows) 或 Cmd+D (Mac) 收藏本导航页，随时获取草榴社区最新网址。
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/category/latest-urls"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 text-primary-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-link text-lg"></i>
                  立即访问
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-700 transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}